import axios from 'axios';
import qs from 'qs';
import _ from 'lodash';
import { loadProgressBar } from 'axios-progress-bar';
import { backendUrl, tsApiUrl } from 'setup';

import { ExchangeApi } from './exchangeApi';
import { store } from 'redux/store';
import { logOut } from 'redux/actions/profile';
import { setErrorStatus } from 'redux/actions/exchangeSettings';

const config = {
  baseURL: backendUrl,
};

const instance = axios.create(config);
const pollingInstance = axios.create(config);
const socialTradeInstance = axios.create({ baseURL: tsApiUrl });

export const setBearer = (bearer) => {
  instance.defaults.headers.common['Authorization'] = bearer?.startsWith('Bearer') ? bearer : `Bearer ${bearer}`;
  pollingInstance.defaults.headers.common['Authorization'] = bearer?.startsWith('Bearer') ? bearer : `Bearer ${bearer}`;
  socialTradeInstance.defaults.headers.common['Authorization'] = bearer?.startsWith('Bearer') ? bearer : `Bearer ${bearer}`;
};

export const apiInstance = async ({ url, method = 'GET', data }) => {
  if (method === 'POST') {
    return instance({
      url,
      method,
      data,
    });
  } else if (method === 'GET') {
    return instance({
      url: `${url}?${qs.stringify(data)}`,
      method,
    });
  }
};

export const authenticatedInstance = async ({ url, method, headers = {}, data, polling = false }) => {
  const willUseInstance = !polling ? instance : pollingInstance;

  const updatedHeaders = {
    ...headers,
  };

  if (method === 'POST') {
    return willUseInstance({
      url,
      method,
      headers: updatedHeaders,
      data,
    });
  } else if (method === 'GET') {
    return willUseInstance({
      url: `${url}?${qs.stringify(data)}`,
      method,
      headers: updatedHeaders,
    });
  }
};

export const getSocialTradeInstance = () => {
  return socialTradeInstance;
};

// Debounce transient "no status" failures (CORS preflight, 502 during
// redeploy, brief network blips) so a flicker doesn't hard-redirect the user
// to /error. We only trip the error page after several consecutive failures
// within a short window — a single successful response resets the counter.
const NETWORK_ERROR_THRESHOLD = 5;
const NETWORK_ERROR_WINDOW_MS = 15_000;
let consecutiveNetworkFailures = 0;
let firstFailureAt = 0;

const noteNetworkSuccess = () => {
  consecutiveNetworkFailures = 0;
  firstFailureAt = 0;
};

const noteNetworkFailure = () => {
  const now = Date.now();
  if (!firstFailureAt || now - firstFailureAt > NETWORK_ERROR_WINDOW_MS) {
    firstFailureAt = now;
    consecutiveNetworkFailures = 1;
    return false;
  }
  consecutiveNetworkFailures += 1;
  return consecutiveNetworkFailures >= NETWORK_ERROR_THRESHOLD;
};

const handleError = (error) => {
  const status = _.get(error, 'response.status');

  if (status === 400) {
    return error.response;
  }

  if (status === 401) {
    const {
      auth: { isAuthenticated },
    } = store.getState();

    if (isAuthenticated) {
      store.dispatch(logOut());
    } else {
      return error.response;
    }
  }
  if (status === 403) {
    return error.response;
  }

  if (!status) {
    // Network-level failure (CORS, DNS, connection refused, 502 without CORS
    // headers on redeploy, etc). Only trip the general error page if these
    // keep happening — a single failure during a BFF restart should not boot
    // the user out of the app.
    if (noteNetworkFailure()) {
      store.dispatch(setErrorStatus());
    }
  }
};

instance.interceptors.response.use((response) => {
  noteNetworkSuccess();
  return response;
}, handleError);

pollingInstance.interceptors.response.use(null, (error) => {
  if (_.get(error, 'response.status') === 401) {
    const {
      auth: { isAuthenticated },
    } = store.getState();

    if (isAuthenticated) {
      store.dispatch(logOut());
    }
  }
});

socialTradeInstance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    return Promise.reject((error.response && error.response.data) || { data: {} });
  },
);

export const exchangeApi = new ExchangeApi({ config, handleError });

window.exchangeApi = exchangeApi;

loadProgressBar({ showSpinner: true }, instance);

window.authenticatedInstance = authenticatedInstance;

export default instance;
